import { useUserSettings } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import type { IntervalListPublicApiQueryOptions } from "@/services/core-api/types"
import { useRenameQueryResult } from "@/services/utils"
import { type HttpError } from "@/utils/api"
import type { UseQueryOptions } from "@tanstack/react-query"
import { useQuery } from "@tanstack/react-query"

import {
  INTERVALS_MAX_PAGES,
  type IntervalsQueryData,
  buildIntervalsQuery,
} from "./intervals-query"

interface UseIntervalsOptions {
  apiQueryOptions: IntervalListPublicApiQueryOptions
  maxPages?: number
  organizationId: Organization["id"]
  /**
   * When false, fetch a single page (capability probe).
   * Default true: page until complete, and error if the safety cap is hit first.
   */
  pageThrough?: boolean
  queryOptions?: Omit<
    UseQueryOptions<IntervalsQueryData, HttpError>,
    "queryKey" | "queryFn"
  >
}

export const useIntervals = ({
  apiQueryOptions,
  maxPages = INTERVALS_MAX_PAGES,
  organizationId,
  pageThrough = true,
  queryOptions = {},
}: UseIntervalsOptions) => {
  const { currency } = useUserSettings()
  const query = buildIntervalsQuery({
    apiQueryOptions,
    currency,
    enabled: queryOptions.enabled !== false,
    maxPages,
    organizationId,
    pageThrough,
  })

  const queryResult = useQuery<IntervalsQueryData, HttpError>({
    ...queryOptions,
    enabled: query.enabled,
    queryFn: query.queryFn,
    queryKey: query.queryKey,
    throwOnError: queryOptions.throwOnError ?? false,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.Intervals)
}
